import { useState, useEffect } from "react";

export interface DeviceCapabilities {
  isMobile: boolean;
  hasCamera: boolean;
  supportsDragAndDrop: boolean;
}

/**
 * Standard signature for all capability detector functions
 * - Must be async (even if doing sync work, for consistency)
 * - Returns a boolean indicating if the capability is available
 * - Can assume browser environment (SSR check handled at hook level)
 */
type CapabilityDetector = () => Promise<boolean>;

/**
 * Detects if the current device is mobile based on user agent and touch support
 */
const detectMobileDevice: CapabilityDetector = async () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  const isMobileUA =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent.toLowerCase(),
    );
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  return isMobileUA || hasTouch;
};

/**
 * Detects if the browser supports drag and drop
 * Returns false for coarse pointer devices (touch-based)
 */
const detectDragAndDropSupport: CapabilityDetector = async () => {
  if (window.matchMedia?.("(pointer: coarse)").matches) {
    return false;
  }

  const div = document.createElement("div");
  return "draggable" in div || ("ondragstart" in div && "ondrop" in div);
};

/**
 * Detects if the device has camera capabilities
 */
const detectCameraAvailability: CapabilityDetector = async () => {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return false;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((device) => device.kind === "videoinput");
  } catch {
    return false;
  }
};

/**
 * Registry of all capability detectors
 *
 * TO ADD A NEW CAPABILITY:
 * 1. Add the new property to DeviceCapabilities interface
 * 2. Create a detector function with CapabilityDetector signature
 * 3. Add it to this registry with the matching key
 * 4. Update the initial state in the hook below
 */
const capabilityDetectors: Record<
  keyof DeviceCapabilities,
  CapabilityDetector
> = {
  isMobile: detectMobileDevice,
  supportsDragAndDrop: detectDragAndDropSupport,
  hasCamera: detectCameraAvailability,
};

/**
 * Hook to detect browser and device capabilities
 * Returns information about device type and available APIs
 *
 * Capabilities are detected once on mount using the detector registry.
 * All detectors run in parallel for optimal performance.
 *
 * SSR-safe: Returns default (false) values if window is undefined.
 */
export const useDeviceCapabilities = (): DeviceCapabilities => {
  const [capabilities, setCapabilities] = useState<DeviceCapabilities>({
    isMobile: false,
    hasCamera: false,
    supportsDragAndDrop: false,
  });

  useEffect(() => {
    const detectCapabilities = async () => {
      if (typeof window === "undefined") {
        return;
      }

      // Run all detectors in parallel for better performance
      const results = await Promise.all(
        Object.entries(capabilityDetectors).map(async ([key, detector]) => {
          const capability = key as keyof DeviceCapabilities;
          const value = await detector();
          return { capability, value };
        }),
      );

      // Build capabilities object from results
      const capabilitiesObject = results.reduce(
        (acc, { capability, value }) => {
          acc[capability] = value;
          return acc;
        },
        {} as DeviceCapabilities,
      );

      setCapabilities(capabilitiesObject);
    };

    detectCapabilities();
  }, []);

  return capabilities;
};
